import { useState } from "react";

import { useFormState } from "common/hooks";
import { CORE_PHONE_LENGTH } from "common/constants";
import { parseNumber } from "common/utils/numbers";
import { toPhone } from "common/utils/formatting";
import { useNavigate } from "common/router/hooks";

import { AuthenticationRedirectAfterLookup, SignupFormValues } from "client/accounts/types";
import PhoneInput from "client/common/components/inputs/PhoneInput";
import Button from "client/common/components/buttons/Button";
import Form from "client/common/components/forms/Form";
import TextButton from "client/common/components/buttons/TextButton";
import TextInput from "client/common/components/inputs/TextInput";
import FetchErrorAlert from "client/common/components/errors/FetchErrorAlert";
import PasswordInput from "client/common/components/inputs/PasswordInput";
import Tooltip from "client/common/components/content/Tooltip";
import { urls } from "client/routes";

import { usernameLockTooltip, passwordConditions } from "../../utils";
import { useProfileLookupMutation, useSendVerificationCodeMutation } from "../../hooks";
import VerifyEmailSignupModal from "./VerifyEmailSignupModal";
import AccountCreationLegalDisclaimer from "./AccountCreationDisclaimer";
import SmsLegalDisclaimer from "./SmsLegalDisclaimer";
import { Optional } from "../../../../common/utils/types";

import styles from "../../styles/CreateAccountEmailAuthForm.module.less";

type CreateAccountEmailAuthFormProps = {
  initialPhone: Optional<string | number>;
  initialEmail: Optional<string>;
  initialFirstName: Optional<string>;
  initialLastName: Optional<string>;
  next?: string;
  /** Lock the email (username) field to the on-file contact when it's on file. */
  lockUsername: boolean;
  /** Whether the user may switch to the SMS signup form (false when pinned to email). */
  canSwitchChannel: boolean;
};

const CreateAccountEmailAuthForm = (props: CreateAccountEmailAuthFormProps) => {
  const {
    initialPhone = "",
    initialEmail = "",
    initialFirstName = "",
    initialLastName = "",
    next,
    lockUsername,
    canSwitchChannel,
  } = props;

  const navigate = useNavigate();

  const [isConfirming, setIsConfirming] = useState(false);

  const [values, updateField] = useFormState<SignupFormValues>({
    first_name: initialFirstName ?? "",
    last_name: initialLastName ?? "",
    phone: initialPhone ? initialPhone.toString() : "",
    email: initialEmail ?? "",
    password: "",
  });

  const isPhoneComplete = values.phone.length >= CORE_PHONE_LENGTH;

  const {
    mutate: sendCode,
    isLoading: isSendingCode,
    error: sendingCodeError,
  } = useSendVerificationCodeMutation({
    // Show the confirmation modal once the code has been sent
    onSuccess: () => setIsConfirming(true),
    signupTracking: { usernameType: "email", isResend: false },
  });

  const exhaustiveCheck = (redirect: never) => {
    console.warn(`Attempted to get authentication redirect for invalid value: ${redirect}`);
  };

  const {
    mutate: lookupProfile,
    isLoading: isLookingUp,
    error: lookupError,
  } = useProfileLookupMutation({
    onSuccess: (result) => {
      switch (result.authentication_redirect) {
        case AuthenticationRedirectAfterLookup.SigninPhone:
          return navigate(
            urls.accounts.login({
              search: {
                flash: `An account already exists for ${toPhone(result.number)}. Please log in, or contact support if you need help signing up.`,
                phone: `${result.number}`,
              },
            }),
          );
        case AuthenticationRedirectAfterLookup.SigninEmail:
          return navigate(
            urls.accounts.login({
              search: {
                flash: `An account already exists for ${result.email}. Please log in, or contact support if you need help signing up.`,
                email: `${result.email}`,
              },
            }),
          );
        case AuthenticationRedirectAfterLookup.Verification:
          return sendCode({
            email: result.email,
            profile: {
              first_name: values.first_name,
              last_name: values.last_name,
              new_password: values.password,
            },
          });
        default:
          return exhaustiveCheck(result.authentication_redirect);
      }
    },
  });

  const error = lookupError || sendingCodeError;
  const isFormComplete = !!(
    values.first_name &&
    values.last_name &&
    values.email &&
    isPhoneComplete &&
    passwordConditions.every(({ check }) => check(values.password))
  );

  const canSubmit = isFormComplete && !isLookingUp && !isSendingCode;
  const isSubmitting = isLookingUp || isSendingCode;
  const onSubmit = () => {
    lookupProfile({
      email: values.email,
      number: parseNumber(values.phone, 0),
      check_sms: false,
    });
  };

  return (
    <>
      <Form className={styles.form} canSubmit={canSubmit} onSubmit={onSubmit}>
        {!!error && <FetchErrorAlert error={error} />}
        <div className={styles.names}>
          <TextInput
            label="First Name"
            value={values.first_name}
            onChange={updateField("first_name")}
            errors={sendingCodeError?.errors.profile?.first_name}
          />
          <TextInput
            label="Last Name"
            value={values.last_name}
            onChange={updateField("last_name")}
            errors={sendingCodeError?.errors.profile?.last_name}
          />
        </div>
        <PhoneInput
          label="Phone Number"
          value={values.phone}
          onChange={updateField("phone")}
          errors={lookupError?.errors.number}
        />
        <Tooltip isEnabled={lockUsername} content={usernameLockTooltip}>
          <TextInput
            label="Email"
            value={values.email}
            onChange={updateField("email")}
            errors={lookupError?.errors.email || sendingCodeError?.errors.email}
            disabled={lockUsername}
          />
        </Tooltip>
        <PasswordInput
          value={values.password}
          onChange={updateField("password")}
          passwordConditions={passwordConditions}
          errors={sendingCodeError?.errors.profile?.new_password}
        />
        <Button type="submit" disabled={!canSubmit} isLoading={isSubmitting}>
          Sign Up
        </Button>
        {canSwitchChannel && (
          <div className={styles.help}>
            Sign up using your mobile number{" "}
            <TextButton
              hasUnderline={true}
              onClick={() => {
                navigate(
                  urls.accounts.signup({
                    search: {
                      last_name: values.last_name,
                      first_name: values.first_name,
                      phone: values.phone,
                      email: values.email,
                      next,
                    },
                  }),
                );
              }}
            >
              here
            </TextButton>
            .
          </div>
        )}
        <AccountCreationLegalDisclaimer textAlign="center" />
        <SmsLegalDisclaimer textAlign="center" />
      </Form>
      <VerifyEmailSignupModal
        visible={isConfirming}
        onClose={() => setIsConfirming(false)}
        values={values}
        next={next}
        lockUsername={lockUsername}
        canSwitchChannel={canSwitchChannel}
        onTryDifferentEmail={() => {
          updateField("email")("");
        }}
        onSendToPhone={() => {
          navigate(
            urls.accounts.signup({
              search: {
                phone: values.phone,
                email: values.email,
                first_name: values.first_name,
                last_name: values.last_name,
                next,
              },
            }),
          );
        }}
      />
    </>
  );
};

export default CreateAccountEmailAuthForm;
